import { getSubunitMultiplier } from './currency';

/**
 * Cash rounding (ops-simulation T07). A rule is a step in currency minor units
 * (50 = 0.050 OMR, 100 = ₹1.00) and a method. It is applied to cash tenders
 * only (T01) and is separate from per-line tax rounding, which stays fixed by
 * the tax regime (docs/multi-currency-plan.md §4).
 */
export const CASH_ROUNDING_MODES = ['HALF_UP', 'UP', 'DOWN'] as const;
export type CashRoundingMode = (typeof CASH_ROUNDING_MODES)[number];

export interface CashRoundingRule {
    /** Step in minor units; 1 = no rounding. */
    step: number;
    mode: CashRoundingMode;
}

/**
 * Smallest coin in use, in minor units — the default step for a new tenant.
 * Unknown codes fall back to one whole major unit.
 */
const SMALLEST_COIN: Record<string, number> = {
    INR: 100, OMR: 5, BHD: 5, KWD: 5, AED: 25, SAR: 5, QAR: 25,
};

/** The default rule for a new tenant: the smallest coin, half up. */
export function defaultCashRoundingRule(currency: string): CashRoundingRule {
    const code = currency.toUpperCase();
    return { step: SMALLEST_COIN[code] ?? getSubunitMultiplier(code), mode: 'HALF_UP' };
}

/**
 * A step is valid for a currency when it divides one whole major unit
 * (5 / 10 / 50 / 100 / 1000 for OMR; 1 / 5 / 10 / 50 / 100 for INR).
 */
export function isValidCashRoundingStep(step: number, currency: string): boolean {
    const subunit = getSubunitMultiplier(currency);
    return Number.isInteger(step) && step > 0 && step <= subunit && subunit % step === 0;
}

/**
 * Round `amount` (integer minor units) to a multiple of `rule.step`. Integer
 * arithmetic only — never via major units, which turns an exact half into
 * x.4999… The magnitude is rounded and the sign restored, so a refund of
 * −5.020 rounds like a sale of 5.020.
 */
export function roundToCashStep(amount: number, rule: CashRoundingRule): number {
    const step = rule.step;
    if (!(step > 1) || amount === 0) return amount;
    const sign = amount < 0 ? -1 : 1;
    const abs = Math.abs(amount);
    const rem = abs % step;
    const base = abs - rem;
    let rounded = base;
    if (rem > 0) {
        if (rule.mode === 'UP') rounded = base + step;
        else if (rule.mode === 'HALF_UP' && rem * 2 >= step) rounded = base + step;
    }
    return sign * rounded;
}

/** The adjustment a cash settlement of `total` takes: rounded − total (signed). */
export function cashRoundingAdjustment(total: number, rule: CashRoundingRule): number {
    return roundToCashStep(total, rule) - total;
}

/** A stored rule; a NULL step means "not set here". */
export interface StoredCashRounding {
    step?: number | null;
    mode?: string | null;
}

function toMode(mode: string | null | undefined): CashRoundingMode {
    return (CASH_ROUNDING_MODES as readonly string[]).includes(mode ?? '') ? (mode as CashRoundingMode) : 'HALF_UP';
}

/**
 * The rule in force at a till: the location override, else the tenant
 * default, else the currency default (smallest coin, half up). Shared by the
 * API and the POS (online and offline) so all three round the same way.
 */
export function resolveCashRoundingRule(
    currency: string,
    tenant?: StoredCashRounding | null,
    location?: StoredCashRounding | null,
): CashRoundingRule {
    if (location?.step != null && location.step > 0) return { step: Number(location.step), mode: toMode(location.mode) };
    if (tenant?.step != null && tenant.step > 0) return { step: Number(tenant.step), mode: toMode(tenant.mode) };
    return defaultCashRoundingRule(currency);
}
